export type OwnedCourseCredit = {
  id: string;
  planId: string | null;
  saleId: string | null;
  orderItemId: string | null;
  courseId: string | null;
  courseName: string;
  planName: string;
  venueName: string;
  remainingCredits: number;
  reservedCredits: number;
  usedCredits: number;
  totalCredits: number;
  validFrom: string;
  validUntil: string | null;
  createdAt: string;
  status: string;
};

export function creditDisplayState(credit: OwnedCourseCredit, now: number) {
  if (credit.status === 'CANCELLED') return '已取消';
  if (credit.status === 'EXPIRED' || (credit.validUntil && Date.parse(credit.validUntil) <= now)) return '已到期';
  if (credit.status === 'FROZEN') return '已凍結';
  if (Date.parse(credit.validFrom) > now) return '尚未生效';
  if (credit.remainingCredits > 0 && credit.status === 'ACTIVE') return '可使用';
  return credit.reservedCredits > 0 ? '待上課' : '已用完';
}

export function isCurrentCredit(credit: OwnedCourseCredit, now: number) {
  // 仍有保留堂數時留在目前方案，避免把尚待核銷的課程藏進歷史。
  return credit.reservedCredits > 0
    || ['可使用', '尚未生效', '已凍結'].includes(creditDisplayState(credit, now));
}

export type OwnedCoursePlan = { id: string; name: string; createdAt: string; credits: OwnedCourseCredit[] };

export function groupOwnedCoursePlans(credits: OwnedCourseCredit[], now: number) {
  const groups = new Map<string, OwnedCoursePlan>();
  for (const credit of credits) {
    // 只依實際購買關聯分組，不能把同名續購或沒有來源的舊額度誤併。
    const purchase = credit.saleId ? `sale:${credit.saleId}` : credit.orderItemId ? `item:${credit.orderItemId}` : `credit:${credit.id}`;
    const id = `${purchase}:plan:${credit.planId ?? 'none'}`;
    const group = groups.get(id) ?? { id, name: credit.planName, createdAt: credit.createdAt, credits: [] };
    group.credits.push(credit);
    if (credit.createdAt < group.createdAt) group.createdAt = credit.createdAt;
    groups.set(id, group);
  }
  const current: OwnedCoursePlan[] = [];
  const history: OwnedCoursePlan[] = [];
  for (const group of groups.values()) {
    group.credits.sort((a, b) => a.courseName.localeCompare(b.courseName, 'zh-TW') || a.id.localeCompare(b.id));
    (group.credits.some(credit => isCurrentCredit(credit, now)) ? current : history).push(group);
  }
  const expiry = (group: OwnedCoursePlan) => Math.min(...group.credits.filter(c => isCurrentCredit(c, now))
    .map(c => c.validUntil ? Date.parse(c.validUntil) : Infinity));
  current.sort((a, b) => (expiry(a) === expiry(b) ? 0 : expiry(a) - expiry(b)) || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
  history.sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id));
  return { current, history };
}
